/**
 * APT 회차 비교 차트 — 순수 계산 로직
 * SPEC: .specify/specs/001-features/apt/compare-chart-spec.md
 *
 * 회차 간 모집단(인원) 수가 다르므로 비율(%) 기준 비교를 기본으로 한다.
 * 위험 단계(링크클릭·정보입력) 비율 감소, 스팸신고 비율 증가를 "개선"으로 본다.
 */

export interface AptStats {
  total: number;
  unread: number;
  emailOpened: number;
  linkClicked: number;
  infoInput: number;
  spamReport: number;
  phishing?: number;
  spam?: number;
}

export type StageDirection = 'lower-better' | 'higher-better' | 'neutral';

export interface StageCompare {
  key: keyof Pick<AptStats, 'unread' | 'emailOpened' | 'linkClicked' | 'infoInput' | 'spamReport'>;
  label: string;
  direction: StageDirection;
  prevCount: number;
  currCount: number;
  prevPct: number; // prev.total 기준 (정수 %)
  currPct: number; // curr.total 기준 (정수 %)
  deltaPct: number; // currPct - prevPct (%포인트)
  improved: boolean; // direction 기준 개선 여부 (neutral은 항상 false)
  newlyMeasured: boolean; // 직전 회차에 측정되지 않은 신규 단계 (예: 정보입력)
}

// canBeNewlyMeasured: 직전 회차에 도입되지 않았을 수 있는(추적 시작) 단계.
// 정보입력은 회차에 따라 측정되지 않았을 수 있어 prev=0을 '악화'가 아닌 '신규 측정'으로 본다.
const STAGE_META: {
  key: StageCompare['key'];
  label: string;
  direction: StageDirection;
  canBeNewlyMeasured?: boolean;
}[] = [
  { key: 'unread', label: '미열람', direction: 'neutral' },
  { key: 'emailOpened', label: '메일열람', direction: 'lower-better' },
  { key: 'linkClicked', label: '링크클릭', direction: 'lower-better' },
  { key: 'infoInput', label: '정보입력', direction: 'lower-better', canBeNewlyMeasured: true },
  { key: 'spamReport', label: '스팸신고', direction: 'higher-better' },
];

/** asc 정렬된 회차 목록에서 current의 직전 회차를 반환. 없으면 null */
export function getPreviousRound(roundsAsc: string[], current: string): string | null {
  const idx = roundsAsc.indexOf(current);
  if (idx <= 0) return null;
  return roundsAsc[idx - 1];
}

/** API 응답(일부 필드 optional)을 모든 필드가 number인 AptStats로 정규화 */
export function normalizeAptStats(s: Partial<AptStats> | null | undefined): AptStats {
  return {
    total: s?.total ?? 0,
    unread: s?.unread ?? 0,
    emailOpened: s?.emailOpened ?? 0,
    linkClicked: s?.linkClicked ?? 0,
    infoInput: s?.infoInput ?? 0,
    spamReport: s?.spamReport ?? 0,
    phishing: s?.phishing ?? 0,
    spam: s?.spam ?? 0,
  };
}

export function emptyAptStats(): AptStats {
  return {
    total: 0,
    unread: 0,
    emailOpened: 0,
    linkClicked: 0,
    infoInput: 0,
    spamReport: 0,
    phishing: 0,
    spam: 0,
  };
}

/** 두 stats를 필드별로 합산 (reduce 누적용) */
export function sumAptStats(a: AptStats, b: AptStats): AptStats {
  return {
    total: a.total + b.total,
    unread: a.unread + b.unread,
    emailOpened: a.emailOpened + b.emailOpened,
    linkClicked: a.linkClicked + b.linkClicked,
    infoInput: a.infoInput + b.infoInput,
    spamReport: a.spamReport + b.spamReport,
    phishing: (a.phishing ?? 0) + (b.phishing ?? 0),
    spam: (a.spam ?? 0) + (b.spam ?? 0),
  };
}

function pct(count: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.round((count / total) * 100);
}

/** prev 대비 curr의 단계별 비교 결과 */
export function buildComparison(prev: AptStats, curr: AptStats): StageCompare[] {
  return STAGE_META.map(({ key, label, direction, canBeNewlyMeasured }) => {
    const prevCount = prev[key];
    const currCount = curr[key];
    const prevPct = pct(prevCount, prev.total);
    const currPct = pct(currCount, curr.total);
    const deltaPct = currPct - prevPct;

    // 직전 회차에 모집단은 있었으나 해당 단계가 전혀 측정되지 않은(0건) 신규 단계
    const newlyMeasured =
      !!canBeNewlyMeasured && prev.total > 0 && prevCount === 0 && currCount > 0;

    let improved = false;
    if (!newlyMeasured) {
      if (direction === 'lower-better') improved = deltaPct < 0;
      else if (direction === 'higher-better') improved = deltaPct > 0;
    }

    return { key, label, direction, prevCount, currCount, prevPct, currPct, deltaPct, improved, newlyMeasured };
  });
}
